import {
  Button,
  Card,
  Code,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ADMIN_LIST_PAGE_SIZE, adminGet } from "../api.ts";
import { AdminTable } from "../components/AdminTable.tsx";

interface AuditRow {
  readonly id: string;
  readonly actorTgUserId: string;
  readonly actorTelegramUsername: string | null;
  readonly actorGroups: ReadonlyArray<string>;
  readonly action: string;
  readonly entity: string;
  readonly entityId: string | null;
  readonly botId: string | null;
  readonly before: unknown;
  readonly after: unknown;
  readonly ip: string | null;
  readonly ua: string | null;
  readonly createdAt: string;
}

interface AuditListResponse {
  readonly records: ReadonlyArray<AuditRow>;
  readonly limit: number;
  readonly offset: number;
}

const fetchAudit = (action: string, entity: string, offset: number): Promise<AuditListResponse> => {
  const params = new URLSearchParams();
  if (action.trim() !== "") params.set("action", action.trim());
  if (entity.trim() !== "") params.set("entity", entity.trim());
  params.set("limit", String(ADMIN_LIST_PAGE_SIZE));
  params.set("offset", String(offset));
  const qs = params.toString();
  return adminGet<AuditListResponse>(`/audit${qs ? `?${qs}` : ""}`);
};

export const AuditLogPage = () => {
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [detailRow, setDetailRow] = useState<AuditRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["audit-log", actionFilter, entityFilter, offset],
    queryFn: () => fetchAudit(actionFilter, entityFilter, offset),
    staleTime: 30_000,
  });

  const records = data?.records ?? [];

  return (
    <Stack gap="md">
      <Title order={2}>审计日志</Title>

      <Group align="flex-end" gap="md">
        <TextInput
          label="操作类型 (action)"
          placeholder="如 retry_grant_outbox"
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.currentTarget.value);
            setOffset(0);
          }}
          style={{ minWidth: 220 }}
        />
        <TextInput
          label="实体类型 (entity)"
          placeholder="如 grant_outbox"
          value={entityFilter}
          onChange={(e) => {
            setEntityFilter(e.currentTarget.value);
            setOffset(0);
          }}
          style={{ minWidth: 220 }}
        />
      </Group>

      <Card withBorder>
        {isLoading ? (
          <Text>加载中...</Text>
        ) : records.length === 0 ? (
          <Text c="dimmed">无记录。</Text>
        ) : (
          <AdminTable minWidth={800}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>时间</Table.Th>
                <Table.Th>操作人</Table.Th>
                <Table.Th>action</Table.Th>
                <Table.Th>entity</Table.Th>
                <Table.Th>entity_id</Table.Th>
                <Table.Th>IP</Table.Th>
                <Table.Th>详情</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {records.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Text size="xs">{new Date(r.createdAt).toLocaleString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {r.actorTelegramUsername !== null
                        ? `@${r.actorTelegramUsername}`
                        : r.actorTgUserId}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {r.actorTgUserId}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {r.action}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {r.entity}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{r.entityId ?? "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{r.ip ?? "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="light" onClick={() => setDetailRow(r)}>
                      详情
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </AdminTable>
        )}
      </Card>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="default"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset((prev) => Math.max(0, prev - ADMIN_LIST_PAGE_SIZE))}
        >
          上一页
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={records.length < ADMIN_LIST_PAGE_SIZE}
          onClick={() => setOffset((prev) => prev + ADMIN_LIST_PAGE_SIZE)}
        >
          下一页
        </Button>
      </Group>

      <Modal
        opened={detailRow !== null}
        onClose={() => setDetailRow(null)}
        title="变更详情"
        size="lg"
      >
        {detailRow !== null ? (
          <Stack gap="sm">
            <Text fw={600} size="sm">
              变更前 (before)
            </Text>
            <Code block>
              {detailRow.before !== null ? JSON.stringify(detailRow.before, null, 2) : "null"}
            </Code>
            <Text fw={600} size="sm">
              变更后 (after)
            </Text>
            <Code block>
              {detailRow.after !== null ? JSON.stringify(detailRow.after, null, 2) : "null"}
            </Code>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
};
